import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { SpanHeadingS } from '@eneco/custom-components/CustomerReview';
import { useCustomerPublicGetCustomerRatings } from '@eneco/dc/hooks';
import { useFormatter } from '@eneco/i18n';
import { useLinkComponent } from '@eneco/link';
import { Bleed, Box, Card, Grid, Heading, Hidden, RatingStar, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import { wrap } from '@sitecore/common';
import { CustomerReviewRendering } from '@sitecore/types/manual/CustomerReview';

const CustomerReview: FC<CustomerReviewRendering> = ({ fields, params }) => {
  const Link = useLinkComponent();
  const { data } = useCustomerPublicGetCustomerRatings(
    fields?.getLiveRatingDataOption?.value === true ? undefined : null,
  );
  const { format, number } = useFormatter();

  const ratingNumber = fields?.getLiveRatingDataOption?.value
    ? data?.averageScore
    : Number(fields?.ratingNumber?.value);
  const starRating = ratingNumber && Math.round(ratingNumber / 2);

  const headingLevel = params?.headingLevel ?? 'h2';

  return (
    <Grid columns={{ md: '2', lg: '3' }} gap="16">
      <Grid.Item>
        <Stack gap="6" alignY={{ sm: 'justify' }}>
          {fields.title?.value && (
            <Heading as={headingLevel} size={{ initial: 'S', md: 'M' }} color="inherit">
              {wrap(fields.title)}
            </Heading>
          )}
          {fields.content?.value && <Text size={{ initial: 'BodyM', md: 'BodyL' }}>{wrap(fields.content)}</Text>}
          <Stack.Item>
            <SpanHeadingS>
              {ratingNumber && starRating && (
                <Stack direction="row" alignY="end" gap="2">
                  <Text>{number.oneFractionDigit(ratingNumber).replace('.', ',')} </Text>
                  <RatingStar maxStars={5} score={ratingNumber} size="large" />
                </Stack>
              )}
            </SpanHeadingS>
            {fields.ratingSubText?.value && fields.link?.value?.href && (
              <Text size={{ initial: 'BodyS' }}>
                {format(fields?.ratingSubText?.value, { numberOfReviews: data?.ratingsAmount || '' })}{' '}
              </Text>
            )}
          </Stack.Item>

          {fields.link?.value?.text && (
            <Box>
              <Link editable={fields.link.editable} linkType={fields.link.value.linktype} linkValue={fields.link.value}>
                <TextLink emphasis="high">{fields.link.value.text}</TextLink>
              </Link>
            </Box>
          )}
        </Stack>
      </Grid.Item>
      {fields.items.map((item, index) => {
        const { fields } = item;

        const maxStars = 5;
        const review = (
          <Bleed top="8" key={index}>
            <Stretch>
              <Grid.Item>
                <Stretch>
                  <Grid gridTemplateColumns="auto auto 24px" gridTemplateRows="32px auto">
                    <Grid.Item gridColumn="1/-1" gridRow="2">
                      <Stretch>
                        <Card>
                          <Box padding={{ initial: '5', md: '10' }} paddingTop="10">
                            <Stack gap="6">
                              {fields.content?.value && (
                                <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                                  {wrap(fields.content, <RichText html={fields.content.value} />)}
                                </Text>
                              )}
                              <Stack.Item>
                                {fields.title?.value && (
                                  <Text size={{ initial: 'BodyS', md: 'BodyM' }} color="textLowEmphasis">
                                    {wrap(fields.title)}
                                  </Text>
                                )}
                                {fields.ratingNumber.value && (
                                  <Stack direction="row" alignY="start" gap="2">
                                    <Text weight="bold" size="BodyL">
                                      {Math.round(Number(fields.ratingNumber.value))}/{maxStars * 2}
                                    </Text>
                                    <RatingStar
                                      maxStars={maxStars}
                                      score={Number(fields.ratingNumber.value)}
                                      size="medium"
                                    />
                                  </Stack>
                                )}
                              </Stack.Item>
                            </Stack>
                          </Box>
                        </Card>
                      </Stretch>
                    </Grid.Item>
                  </Grid>
                </Stretch>
              </Grid.Item>
            </Stretch>
          </Bleed>
        );

        return index === 1 ? (
          <Hidden below="lg" key={index}>
            {review}
          </Hidden>
        ) : (
          review
        );
      })}
    </Grid>
  );
};

export default CustomerReview;
